import { buildListConsumedToolDeployments } from '@efdigital/ll-admin-v2/data/builders/lti/lti-consumed-tool-deployments';
import { buildListConsumedTools } from '@efdigital/ll-admin-v2/data/builders/lti/lti-consumed-tools';
import { buildFindConsumingPlatform } from '@efdigital/ll-admin-v2/data/builders/lti/lti-consuming-platforms';
import { DataTypeLti } from '@efdigital/ll-admin-v2/data/data-types/lti';
import type {
  LtiConsumedTool,
  LtiConsumedToolDeployment,
  LtiConsumingPlatform,
} from '@efdigital/ll-admin-v2/data/types/lti';
import type { LibraryClientsEditModel } from '@efdigital/ll-admin-v2/routes/library/clients/client';
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';

export type LibraryClientsEditLtiIntegrationsPlatformModel =
  LibraryClientsEditModel & {
    platform: LtiConsumingPlatform;
    tools: LtiConsumedTool[];
    deploymentsByToolId: Record<string, LtiConsumedToolDeployment[]>;
  };

export default class LibraryClientsClientEditLtiIntegrationsPlatformRoute extends Route<LibraryClientsEditLtiIntegrationsPlatformModel> {
  @service declare store: Store;

  controllerName = 'library.clients.client.edit';

  async model(params: {
    platform_id: string;
  }): Promise<LibraryClientsEditLtiIntegrationsPlatformModel> {
    const parentModel = this.modelFor(
      'library.clients.client.edit'
    ) as LibraryClientsEditModel;

    let platform = this.store.peekRecord<LtiConsumingPlatform>(
      DataTypeLti.ConsumingPlatform,
      params.platform_id
    );

    if (!platform) {
      platform = (
        await this.store.request(buildFindConsumingPlatform(params.platform_id))
      ).content.data;
    }

    if (!platform) {
      throw new Error(`Platform ${params.platform_id} not found`);
    }

    const tools = (
      await this.store.request(buildListConsumedTools(platform.id))
    ).content.data;

    const deploymentEntries = await Promise.all(
      tools.map(async (tool) => {
        const deployments = (
          await this.store.request(
            buildListConsumedToolDeployments(platform.id, tool.id)
          )
        ).content.data;
        return [tool.id, deployments] as const;
      })
    );

    const deploymentsByToolId = Object.fromEntries(deploymentEntries);

    return {
      ...parentModel,
      platform,
      tools,
      deploymentsByToolId,
    };
  }
}
